import snakecaseKeys from 'snakecase-keys'
import camelcaseKeys from 'camelcase-keys'
import type { FetchResponse } from 'ofetch'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { cloneDeep } from 'es-toolkit'

dayjs.extend(timezone)
dayjs.extend(utc)

export function transformApiRequest(data: any) {
  const clonedData = cloneDeep(data)
  parseDateRequest(clonedData)

  if (clonedData) {
    return snakecaseKeys(clonedData, { deep: true, parsingOptions: { locale: false } })
  } else {
    return clonedData
  }
}

const noTransformRegex = /_no_transform$/

function removeNoTransformSuffix(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(removeNoTransformSuffix)
  }

  if (obj !== null && typeof obj === 'object') {
    return Object.entries(obj).reduce(
      (acc, [key, value]) => {
        // Remove _no_transform suffix if present
        const newKey = key.replace(noTransformRegex, '')

        // Recursively process nested objects/arrays
        acc[newKey] = removeNoTransformSuffix(value)
        return acc
      },
      {} as Record<string, any>
    )
  }

  return obj
}

export function transformApiResponse(data: any, parseJson: boolean = false) {
  if (!data) {
    return data
  }

  if (parseJson) {
    data = JSON.parse(data)
  }

  data = camelcaseKeys(data, {
    deep: true,
    stopPaths: ['errors'],
    exclude: [/.*_no_transform/g]
  })

  data = removeNoTransformSuffix(data)

  parseDateResponse(data)

  return data
}

export function checkForTokenRefresh(
  authHeader: string | undefined | null,
  setAuthToken: (token: string) => void
) {
  if (authHeader) {
    const startOfToken = authHeader.indexOf(' ') + 1
    const token = authHeader.slice(startOfToken)

    setAuthToken(token)
  }
}

export async function processApiError(
  errorResponse: FetchResponse<any> | string,
  setAuthToken: (token: string) => void,
  logout: () => void
) {
  // All we can do here, time to return
  if (typeof errorResponse === 'string') {
    return Promise.reject(errorResponse)
  }

  const route = window.location.pathname

  if (errorResponse.headers.has('authorization')) {
    checkForTokenRefresh(errorResponse.headers.get('authorization'), setAuthToken)
  }

  if (errorResponse.status === 401 && !route.includes('login')) {
    logout()
    const router = useRouter()
    await router?.isReady()

    // Router can be undefined here, randomly, not sure why.
    // In case it is, we use the old-school method of going to another page.
    if (router) {
      await router.push({
        name: 'login',
        query: {
          redirect: route
        }
      })
    } else {
      window.location.href = `https://${window.location.host}/login?redirect=${route}`
    }
  }

  return Promise.reject(errorResponse)
}

export function parseDateRequest(body: any, internalCount = 0) {
  if (
    body === null ||
    body === undefined ||
    typeof body !== 'object' ||
    internalCount > 10
  ) {
    return body
  }

  for (const key of Object.keys(body)) {
    const value = body[key]
    if (dayjs.isDayjs(value)) {
      body[key] = value.tz('Etc/UTC', true).toISOString()
    } else if (typeof value === 'object') {
      parseDateRequest(value, internalCount + 1)
    }
  }

  return body
}

const isoDateFormat =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)((-(\d{2}):(\d{2})|Z)?)$/
const simpleDateFormat = /^\d{4}-(0?[1-9]|1[012])-(0?[1-9]|[12][0-9]|3[01])$/

function isIsoDateString(value: any): boolean {
  return value && typeof value === 'string' && isoDateFormat.test(value)
}

function isSimpleDateString(value: any): boolean {
  return value && typeof value === 'string' && simpleDateFormat.test(value)
}

export function parseDateResponse(body: any, internalCount = 0) {
  if (
    body === null ||
    body === undefined ||
    typeof body !== 'object' ||
    internalCount > 10
  ) {
    return body
  }

  for (const key of Object.keys(body)) {
    const value = body[key]
    if (isIsoDateString(value)) {
      body[key] = dayjs(value)
    } else if (isSimpleDateString(value)) {
      body[key] = dayjs(value)
    } else if (typeof value === 'object') {
      parseDateResponse(value, internalCount + 1)
    }
  }

  return body
}
